(() => {
  const selector = 'form[data-shipping-preference-cart]';
  const endpoint = `${window.location.origin}/api/2026-07/graphql.json`;
  const mutation = `
    mutation SetShippingPreference($cartId: ID!, $buyerIdentity: CartBuyerIdentityInput!) {
      cartBuyerIdentityUpdate(cartId: $cartId, buyerIdentity: $buyerIdentity) {
        cart {
          checkoutUrl
          buyerIdentity {
            preferences {
              delivery {
                deliveryMethod
              }
            }
          }
        }
        userErrors {
          field
          message
          code
        }
        warnings {
          message
          code
          target
        }
      }
    }
  `;

  function restoreSubmitter(submitter, wasDisabled) {
    if (!submitter) return;

    submitter.disabled = wasDisabled;
    submitter.removeAttribute('aria-busy');
  }

  function stopFrozenCheckout(form, submitter, wasDisabled, error) {
    delete form.dataset.shippingPreferencePending;
    form.inert = false;
    restoreSubmitter(submitter, wasDisabled || error.code === 'FROZEN_CART_MIXED');
    window.hugcoffeeCartGifts?.releaseCheckout();
    window.hugcoffeeFrozenCart?.syncMarkup();
    if (error.code === 'FROZEN_CART_MIXED') return;
    if (window.hugcoffeeCartGifts) window.hugcoffeeCartGifts.showError(error.message);
    else {
      let message = form.querySelector('[data-cart-validation-error]');
      if (!message) {
        message = document.createElement('p');
        message.setAttribute('data-cart-validation-error', '');
        message.setAttribute('role', 'alert');
        form.prepend(message);
      }
      message.textContent = error.message;
    }
  }

  function continueWithNativeCheckout(form, submitter, wasDisabled) {
    form.inert = false;
    delete form.dataset.shippingPreferencePending;
    form.dataset.shippingPreferenceBypass = 'true';
    restoreSubmitter(submitter, wasDisabled);

    if (typeof form.requestSubmit === 'function') {
      form.requestSubmit(submitter);
      return;
    }

    const checkoutInput = document.createElement('input');
    checkoutInput.type = 'hidden';
    checkoutInput.name = 'checkout';
    form.append(checkoutInput);
    HTMLFormElement.prototype.submit.call(form);
  }

  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    document.querySelectorAll(selector).forEach((form) => {
      if (form.dataset.shippingPreferenceWasDisabled === undefined) return;
      delete form.dataset.shippingPreferencePending;
      delete form.dataset.shippingPreferenceBypass;
      form.inert = false;
      restoreSubmitter(form.querySelector('[name="checkout"]'), form.dataset.shippingPreferenceWasDisabled === 'true');
      window.hugcoffeeCartGifts?.releaseCheckout();
    });
  });

  document.addEventListener('submit', async (event) => {
    const form = event.target.closest?.(selector);
    const submitter = event.submitter;

    if (!form || submitter?.name !== 'checkout') return;

    if (form.dataset.shippingPreferenceBypass === 'true') {
      delete form.dataset.shippingPreferenceBypass;
      return;
    }

    event.preventDefault();

    if (form.dataset.shippingPreferencePending === 'true') return;

    form.dataset.shippingPreferencePending = 'true';
    const wasDisabled = submitter.disabled;
    form.dataset.shippingPreferenceWasDisabled = String(wasDisabled);
    submitter.disabled = true;
    submitter.setAttribute('aria-busy', 'true');
    form.inert = true;

    // Validate both gift types through the existing shared queue before SHIPPING.
    // A cart validation/network error must not fall through to unchecked checkout.
    try {
      if (!window.hugcoffeeFrozenCart) {
        const error = new Error('カート内容を確認できません。ページを再読み込みしてください。');
        error.code = 'FROZEN_CART_CHECK_FAILED';
        throw error;
      }
      await window.hugcoffeeFrozenCart.prepareCheckout();
      if (!window.hugcoffeeCartGifts) throw new Error('カートを確認できません。ページを再読み込みしてください。');
      await window.hugcoffeeCartGifts.prepareCheckout();
      if (!window.hugcoffeeDeliveryRequest) throw new Error('お届け希望日時を確認できません。ページを再読み込みしてください。');
      await window.hugcoffeeDeliveryRequest.prepareCheckout();
      await window.hugcoffeeFrozenCart.prepareCheckout();
    } catch (error) {
      if (error.code === 'FROZEN_CART_MIXED' || error.code === 'FROZEN_CART_CHECK_FAILED') {
        stopFrozenCheckout(form, submitter, wasDisabled, error);
        return;
      }
      delete form.dataset.shippingPreferencePending;
      form.inert = false;
      restoreSubmitter(submitter, wasDisabled);
      window.hugcoffeeCartGifts?.releaseCheckout();
      if (window.hugcoffeeCartGifts) window.hugcoffeeCartGifts.showError(error.message);
      else {
        let message = form.querySelector('[data-cart-validation-error]');
        if (!message) {
          message = document.createElement('p');
          message.setAttribute('data-cart-validation-error', '');
          message.setAttribute('role', 'alert');
          form.prepend(message);
        }
        message.textContent = error.message;
      }
      return;
    }

    try {
      if (!window.Shopify?.actions?.getCart) {
        throw new Error('Shopify Storefront Actions are unavailable.');
      }

      const { cart } = await window.Shopify.actions.getCart();

      if (!cart?.id) {
        throw new Error('The current cart ID could not be resolved.');
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query: mutation,
          variables: {
            cartId: cart.id,
            buyerIdentity: {
              preferences: {
                delivery: {
                  deliveryMethod: ['SHIPPING'],
                },
              },
            },
          },
        }),
      });

      if (!response.ok) {
        throw new Error(`Storefront API request failed with status ${response.status}.`);
      }

      const result = await response.json();

      if (result.errors?.length) {
        throw new Error(result.errors.map((error) => error.message).join(' '));
      }

      const payload = result.data?.cartBuyerIdentityUpdate;

      if (payload?.userErrors?.length) {
        throw new Error(payload.userErrors.map((error) => error.message).join(' '));
      }

      if (payload?.warnings?.length) {
        console.warn('Shipping preference warnings:', payload.warnings);
      }

      const deliveryMethods = payload?.cart?.buyerIdentity?.preferences?.delivery?.deliveryMethod;
      const checkoutUrl = payload?.cart?.checkoutUrl;

      if (!Array.isArray(deliveryMethods) || !deliveryMethods.includes('SHIPPING') || !checkoutUrl) {
        throw new Error('The shipping preference or checkout URL was not confirmed.');
      }

      await window.hugcoffeeFrozenCart.prepareCheckout();
      window.location.assign(checkoutUrl);
    } catch (error) {
      if (error.code === 'FROZEN_CART_MIXED' || error.code === 'FROZEN_CART_CHECK_FAILED') {
        stopFrozenCheckout(form, submitter, wasDisabled, error);
        return;
      }
      console.warn('Shipping preference could not be set; continuing with standard checkout.', error);
      try {
        await window.hugcoffeeFrozenCart.prepareCheckout();
      } catch (failure) {
        stopFrozenCheckout(form, submitter, wasDisabled, failure);
        return;
      }
      continueWithNativeCheckout(form, submitter, wasDisabled);
    }
  });
})();
